'use client';
import { useLayoutEffect, useRef, useState } from 'react';
import type { HTMLAttributes, ReactElement, Ref } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { DIVIDER, type ActionMenuEntry } from './ActionMenu';
import { assertMenuEntries, resolveMenuLook, type MenuItemLook, type ResolvedMenuLook } from './menuActions';
import { useTranslation } from '../../lib/i18n';
import { useLongPress, type LongPressPoint } from './longPress';
import { Input } from './shadcn/input';
import {
  ContextMenu as ContextMenuRoot,
  ContextMenuCheckboxItem,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger as RadixContextMenuTrigger,
} from './shadcn/context-menu';

/** A clickable action row. `checked` (true or false) makes it a toggle row that shows its state with a
 *  trailing check and reads as `menuitemcheckbox`; left out, the row is a plain action. */
type MenuAction = { onClick: () => void; danger?: boolean; disabled?: boolean; checked?: boolean } & MenuItemLook;
/** A row that expands a nested panel of its own entries on hover/click. */
type MenuSubmenu = { disabled?: boolean; items: MenuEntry[] } & Exclude<MenuItemLook, { checked: boolean }>;
/** A row that turns into a text field in place when chosen (a rename): Enter commits the value, Escape
 *  closes the menu. `onCommit` resolves true once the value is saved, which closes the menu; false keeps
 *  the field open with the typed value, so the caller reports the failure and the reader can retry. */
type MenuEdit = { disabled?: boolean; edit: { value: string; onCommit: (value: string) => Promise<boolean> } } & Exclude<MenuItemLook, { checked: boolean }>;
export type MenuEntry = MenuAction | MenuSubmenu | MenuEdit | typeof DIVIDER;

export interface ContextMenuState { x: number; y: number; items: MenuEntry[] }

/** Convert the kebab grammar (`ActionMenu` entries) into this menu's rows: leaf actions keep their look
 *  (vocabulary action or own icon), disabled, destructive and checked state; submenus convert recursively
 *  and the divider passes through. Every row here runs after the menu closed, so a leaf's `onSelect` and
 *  then its `onAfterClose` run together as the row's action. This is an internal web adapter, not part of
 *  the plugin UI runtime. */
export function toMenuEntries(items: readonly ActionMenuEntry[]): MenuEntry[] {
  return items.map((item): MenuEntry => {
    if (item === DIVIDER) return DIVIDER;
    if ('items' in item) {
      const { id: _id, items: nested, ...submenu } = item;
      return { ...submenu, items: toMenuEntries(nested) };
    }
    const { id: _id, tone, onSelect, onAfterClose, ...action } = item;
    return { ...action, ...(tone === 'danger' ? { danger: true } : {}), onClick: onAfterClose ? () => { onSelect(); onAfterClose(); } : onSelect };
  });
}

/** Where a menu opens, in viewport pixels. */
export type ContextMenuPoint = LongPressPoint;

/** Text the reader edits, where the browser's own menu (paste, spelling) is the one that belongs. */
const EDITABLE = 'textarea, select, [contenteditable]:not([contenteditable="false"]), input:not([type="checkbox"], [type="radio"], [type="button"], [type="submit"], [type="reset"], [type="range"], [type="color"], [type="file"], [type="image"])';

/** Whether the app's menu belongs on this target: not on a text field, not on an element holding text the
 *  reader has selected (the browser's menu copies it), and not inside an open menu panel. */
function opensHere(target: Element): boolean {
  if (target.closest(EDITABLE) || target.closest('[role="menu"]')) return false;
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed) return true;
  for (let i = 0; i < selection.rangeCount; i += 1) {
    if (selection.getRangeAt(i).intersectsNode(target)) return false;
  }
  return true;
}

const SELF = 'self';

/** What a parent rendering this trigger `asChild` may hand it, forwarded to the child element. */
type SlotForward = Omit<HTMLAttributes<HTMLElement>, 'children'> & { ref?: Ref<HTMLElement> };

/** The one way to open a positional `ContextMenu` from an element: a right-click under the pointer, the
 *  Menu key or Shift+F10 under the focused element's bottom-left, and a touch long press (see
 *  `useLongPress`) under the finger — iOS included, which raises no `contextmenu` of its own, and Android
 *  without a second open from the menu it does raise. It renders no element: its handlers are merged onto
 *  its single child (Radix `Slot`, the child's own handlers first), so it wraps a card, a row or a whole
 *  surface without changing the markup. The innermost trigger owns an event; an enclosing one ignores it,
 *  and so does a trigger whose React subtree merely hosts a portal (a dialog) the event came from.
 *
 *  `disabled` leaves everything to the browser. Text fields and selected text always keep the browser's
 *  menu. A control inside whose own hold is another gesture (a drag handle) stops its `pointerdown` and
 *  its `contextmenu` from reaching the trigger. */
export function ContextMenuTrigger({ onOpen, disabled = false, children, ...slotProps }: {
  onOpen: (point: ContextMenuPoint) => void;
  disabled?: boolean;
  children: ReactElement;
} & SlotForward) {
  const press = useLongPress({
    enabled: !disabled,
    subjectOf: (target) => (opensHere(target) ? SELF : null),
    onLongPress: (_subject, point) => onOpen(point),
  });
  // A parent `asChild` (a Radix dialog, the sidebar primitive) hands its props and ref to this trigger as
  // to any element; they reach the child through the Slot, their handlers running before this trigger's.
  const { onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onMouseDownCapture, onClickCapture, onContextMenu, onKeyDown, ...rest } = slotProps;
  return (
    <Slot
      {...rest}
      {...(disabled ? {} : { 'data-context-menu-trigger': '' })}
      onPointerDown={(event) => { onPointerDown?.(event); press.onPointerDown(event); }}
      onPointerMove={(event) => { onPointerMove?.(event); press.onPointerMove(event); }}
      onPointerUp={(event) => { onPointerUp?.(event); press.onPointerUp(); }}
      onPointerCancel={(event) => { onPointerCancel?.(event); press.onPointerCancel(); }}
      onMouseDownCapture={(event) => { onMouseDownCapture?.(event); press.releaseMouseDown(event); }}
      onClickCapture={(event) => { onClickCapture?.(event); press.releaseClick(event); }}
      onContextMenu={(event) => {
        onContextMenu?.(event);
        if (disabled || event.defaultPrevented) return;
        if (press.contextMenu(event)) { event.stopPropagation(); return; }
        if (!(event.target instanceof Element) || !event.currentTarget.contains(event.target) || !opensHere(event.target)) return;
        event.preventDefault();
        event.stopPropagation();
        onOpen({ x: event.clientX, y: event.clientY });
      }}
      onKeyDown={(event) => {
        onKeyDown?.(event);
        if (disabled || event.defaultPrevented) return;
        if (event.key !== 'ContextMenu' && !(event.shiftKey && event.key === 'F10')) return;
        if (!(event.target instanceof Element) || !event.currentTarget.contains(event.target) || !opensHere(event.target)) return;
        event.preventDefault();
        event.stopPropagation();
        const box = event.target.getBoundingClientRect();
        onOpen({ x: box.left, y: box.bottom });
      }}
    >
      {children}
    </Slot>
  );
}

function isSubmenu(e: MenuEntry): e is MenuSubmenu { return e !== DIVIDER && 'items' in e; }
function isEdit(e: MenuEntry): e is MenuEdit { return e !== DIVIDER && 'edit' in e; }

/** A floating right-click menu with optional submenus, composed from the shadcn/ui `ContextMenu` parts
 *  in `./shadcn/context-menu.tsx`. Closes on outside click, Esc, or after a leaf item runs. Placement is
 *  clamped to the viewport and each submenu flips left/up when it would overflow — all of that is Radix's
 *  collision handling now, not hand-measured here. Opened from `ContextMenuTrigger`.
 *
 *  The public shape is deliberately positional: a call site owns the open state and hands over the
 *  coordinates of the right-click, key or long press that produced it. Radix's ContextMenu anchors to a
 *  `contextmenu` EVENT rather than to coordinates, so the bridge between the two is the hidden trigger
 *  below: mounting this component replays the right-click on it at the requested point, which is exactly
 *  the input Radix expects and what makes its virtual anchor land under the cursor. Doing it this way
 *  means the zoom compensation this file used to carry is gone on purpose — Floating UI reads the
 *  ancestor scale itself, so the arithmetic no longer belongs here.
 *
 *  A chosen action runs AFTER the menu closed and focus went back to the element that held it when the
 *  menu opened (the row a Menu key or Shift+F10 opened it from). An action that opens a dialog therefore
 *  hands that dialog a stable element to return focus to, never a menu row about to unmount. A menu
 *  dismissed by a press elsewhere leaves focus where that press put it. */
export function ContextMenu({ state, onClose, label }: {
  state: ContextMenuState;
  onClose: () => void;
  /** The panel's accessible name (a register row's "Name: More actions"). */
  label?: string;
}) {
  const { t } = useTranslation();
  assertMenuEntries(state.items, t);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  const chosen = useRef<(() => void) | null>(null);
  const dismissedOutside = useRef(false);

  useLayoutEffect(() => {
    // Re-anchoring an open menu (a second right-click) keeps the opener it already has.
    const focused = document.activeElement;
    if (focused instanceof HTMLElement && !contentRef.current?.contains(focused)) opener.current = focused;
    dismissedOutside.current = false;
    triggerRef.current?.dispatchEvent(new MouseEvent('contextmenu', {
      bubbles: true,
      cancelable: true,
      clientX: state.x,
      clientY: state.y,
    }));
  }, [state.x, state.y]);

  return (
    <ContextMenuRoot
      // Not modal: this menu is opened from inside dialogs that already own the page's scroll lock and
      // background isolation, and a second lock fighting the first is how a dialog ends up unscrollable
      // after a right-click. Outside presses still dismiss it.
      modal={false}
      onOpenChange={(open) => { if (!open) onClose(); }}
    >
      <RadixContextMenuTrigger ref={triggerRef} className="hidden" aria-hidden />
      <ContextMenuContent
        ref={contentRef}
        aria-label={label}
        onInteractOutside={() => { dismissedOutside.current = true; }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          if (!dismissedOutside.current && opener.current?.isConnected) opener.current.focus({ preventScroll: true });
          const run = chosen.current;
          chosen.current = null;
          run?.();
        }}
      >
        {state.items.map((item, i) => <MenuRow key={i} entry={item} choose={(run) => { chosen.current = run; }} onClose={onClose} />)}
      </ContextMenuContent>
    </ContextMenuRoot>
  );
}

type Choose = (run: () => void) => void;

/** A row's icon (or the spacer that keeps a choice-list row's label in line) and its label. */
function MenuRowLabel({ look, grow = false }: { look: ResolvedMenuLook; grow?: boolean }) {
  const Icon = look.icon;
  return <>
    {look.iconNode ?? (Icon ? <Icon size={13} aria-hidden /> : <span className="w-3.25" aria-hidden />)}
    <span className={grow ? 'min-w-0 flex-1 break-words' : 'min-w-0 break-words'}>{look.label}</span>
  </>;
}

/** One row of a menu panel: a divider, a leaf action, a toggle, or a submenu that expands a nested panel.
 *  Selecting a leaf closes the whole menu — Radix does that, which is what reaches `onClose` — and hands
 *  its action to the menu, which runs it once the panel is gone. */
function MenuRow({ entry, choose, onClose }: { entry: MenuEntry; choose: Choose; onClose: () => void }) {
  if (entry === DIVIDER) return <ContextMenuSeparator />;
  if (isSubmenu(entry)) return <SubmenuRow entry={entry} choose={choose} onClose={onClose} />;
  if (isEdit(entry)) return <EditRow entry={entry} onClose={onClose} />;
  return <ActionRow entry={entry} choose={choose} />;
}

function ActionRow({ entry, choose }: { entry: MenuAction; choose: Choose }) {
  const { t } = useTranslation();
  const look = resolveMenuLook(entry, t);
  const content = <MenuRowLabel look={look} />;
  if (entry.checked !== undefined) {
    return (
      <ContextMenuCheckboxItem
        checked={entry.checked}
        disabled={entry.disabled}
        variant={look.danger ? 'destructive' : 'default'}
        onSelect={() => choose(entry.onClick)}
      >
        {content}
      </ContextMenuCheckboxItem>
    );
  }
  return (
    <ContextMenuItem
      disabled={entry.disabled}
      variant={look.danger ? 'destructive' : 'default'}
      onSelect={() => choose(entry.onClick)}
    >
      {content}
    </ContextMenuItem>
  );
}

/** A submenu row: hovering (or clicking, for touch; ArrowRight on the keyboard) expands a nested panel. */
function SubmenuRow({ entry, choose, onClose }: { entry: MenuSubmenu; choose: Choose; onClose: () => void }) {
  const { t } = useTranslation();
  const look = resolveMenuLook(entry, t);
  return (
    <ContextMenuSub>
      <ContextMenuSubTrigger disabled={entry.disabled}>
        <MenuRowLabel look={look} grow />
      </ContextMenuSubTrigger>
      <ContextMenuSubContent>
        {entry.items.map((item, i) => <MenuRow key={i} entry={item} choose={choose} onClose={onClose} />)}
      </ContextMenuSubContent>
    </ContextMenuSub>
  );
}

/** An edit row: a menu item until chosen, then a text field in its place while the menu stays open.
 *
 *  The field keeps its keys to itself: the menu content's own key handling moves focus to the next item
 *  on an arrow key, out of the field mid-edit. Escape is the exception, so the menu's own dismissal still
 *  closes it. */
function EditRow({ entry, onClose }: { entry: MenuEdit; onClose: () => void }) {
  const [value, setValue] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const { t } = useTranslation();
  const look = resolveMenuLook(entry, t);
  if (value === null) {
    return (
      <ContextMenuItem
        disabled={entry.disabled}
        // Choosing the row opens the field instead of closing the menu.
        onSelect={(event) => { event.preventDefault(); setValue(entry.edit.value); }}
      >
        <MenuRowLabel look={look} />
      </ContextMenuItem>
    );
  }
  const commit = async () => {
    const next = value.trim();
    if (!next || saving) return;
    if (next === entry.edit.value) { onClose(); return; }
    setSaving(true);
    const saved = await entry.edit.onCommit(next);
    if (saved) onClose();
    else setSaving(false);
  };
  return (
    <div className="p-1">
      <Input
        autoFocus
        aria-label={look.label}
        value={value}
        disabled={saving}
        aria-invalid={value.trim() === '' || undefined}
        onFocus={(event) => event.currentTarget.select()}
        onChange={(event) => setValue(event.currentTarget.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') return;
          event.stopPropagation();
          if (event.key === 'Enter') { event.preventDefault(); void commit(); }
        }}
        className="w-56"
      />
    </div>
  );
}
